import { T, useTranslate } from '@tolgee/react';
import { Collapse, Dropdown, Menu, Tooltip } from 'antd';
import { groupBy } from 'lodash';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useSession } from 'next-auth/react';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import Hotkeys from 'react-hot-keys';
import QRCode from 'react-qr-code';
import styled, { css } from 'styled-components';

import {
  CheckSvg,
  CopySvg,
  CrossSvg,
  EyeOffSvg,
  UserSvg,
} from '../../../../assets/icons';
import AppreciateSvg from '../../../../assets/icons/AppreciateSvg';
import ArrowRightSvg from '../../../../assets/icons/ArrowRightSvg';
import FlashSvg from '../../../../assets/icons/FlashSvg';
import GameSettingsSvg from '../../../../assets/icons/GameSettingsSvg';
import IdeasSvg from '../../../../assets/icons/IdeasSvg';
import LaptopSvg from '../../../../assets/icons/LaptopSvg';
import MobileSvg from '../../../../assets/icons/MobileSvg';
import StreamingSvg from '../../../../assets/icons/StreamingSvg';
import theme from '../../../../assets/theme';
import { Button } from '../../../../components/_atoms/Button';
import DismissableQuestionList from '../../../../components/_atoms/DismissableQuestionList';
import { LoadingDots } from '../../../../components/_atoms/DismissableQuestionList/DismissableQuestionList';
import { DropdownButtonItem } from '../../../../components/_atoms/DropdownButton/DropdownButton';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import StreamingLongAnswerList from '../../../../components/_atoms/StreamingLongAnswerList';
import ActivationOverlay from '../../../../components/_molecules/ActivityOverlay';
import LiveEmptyState from '../../../../components/_molecules/LiveEmptyState';
import { getRoundState } from '../../../../components/_organisms/LiveGame';
import Loader from '../../../../components/Loader';
import {
  PollOptions,
  Slide,
  SlideType,
} from '../../../../components/SlideComponents/slide.types';
import GameAPI from '../../../../frontendServices/gameAPI';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useDismissResponse from '../../../../hooks/useDismissResponse';
import useGameListener from '../../../../hooks/useGameListener';
import { useGameParticipants } from '../../../../hooks/useGameParticipants';
import useKickParticipants from '../../../../hooks/useKickParticipants';
import { useMetrics } from '../../../../hooks/useMetrics';
import useMixpanelFlag from '../../../../hooks/useMixpanelFlag';
import useResponses from '../../../../hooks/useResponses';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import NoAccess from '../../../../providers/NoAccess';
import PoorInternetStatusProvider from '../../../../providers/PoorInternetStatusProvider';
import SocketProvider from '../../../../providers/SocketProvider';
import TimesyncProvider from '../../../../providers/TimesyncProvider';
import { TimesyncContext } from '../../../../providers/TimesyncProvider/TimesyncProvider';
import {
  ById,
  Game,
  PartialParticipant,
} from '../../../../services/backendService/types';
import { CenteredContainer } from '../../../../styles/CenteredContainer';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { zIndexes } from '../../../../utils/zIndexes';

const { Panel } = Collapse;

const ActionButton = styled.button<{ $backgroundColor?: string; $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 48px;
  color: ${({ $color }) => ($color ? $color : theme.colors.black)};
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : theme.colors.white};

  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  & svg {
    height: 20px;
    width: 20px;
  }
  border: none;
  border-radius: 20px;
  outline: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover {
    box-shadow: 0px 2px 40px rgba(0, 0, 0, 0.2);
  }
`;

const ActionBar = ({
  onShowParticipantsToggled,
  isShowingParticipants,
  gameId,
}: {
  onShowParticipantsToggled: () => void;
  isShowingParticipants: boolean;
  gameId: string;
}) => {
  const { tenantId } = useRouterMatch();
  const { t } = useTranslate();
  const metrics = useMetrics();
  const [showGameSettingsDropdown, setShowGameSettingsDropdown] = useState(false);

  const handleToggleParticipants = () => {
    metrics.logEvent('Curimote.ToggleParticipants');
    onShowParticipantsToggled();
  };

  const toCuripodLive = () => {
    window.open(`${window.location.origin}/${tenantId}/games/${gameId}/live`, 'curipod');
  };

  const gameSettingsMenu = (
    <Menu>
      <Menu.Item key="showparticipants">
        <DropdownButtonItem
          isDarkTheme={false}
          icon={<UserSvg />}
          title={isShowingParticipants ? t('Hide participants') : t('Show participants')}
          description={t('Toggle at any time during a lesson')}
          includeSwitch
          switchValue={isShowingParticipants}
          onSwitchToggled={handleToggleParticipants}
        />
      </Menu.Item>
      <Menu.Item key="openlive" onClick={toCuripodLive}>
        <DropdownButtonItem
          isDarkTheme={false}
          icon={<FlashSvg />}
          title={t('Open Curipod live')}
          description={t('This will open Curipod live in another window')}
        />
      </Menu.Item>
    </Menu>
  );

  return (
    <ActionBarContainer>
      <Dropdown
        placement="bottomRight"
        visible={showGameSettingsDropdown}
        onVisibleChange={setShowGameSettingsDropdown}
        trigger={['click']}
        overlay={gameSettingsMenu}
      >
        <Tooltip title={t('Open settings')}>
          <ActionButton>
            <GameSettingsSvg />
          </ActionButton>
        </Tooltip>
      </Dropdown>
    </ActionBarContainer>
  );
};

const ActionBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  & > * {
    margin-left: 10px;
  }
`;

const VotingView = ({
  gameId,
  tenantId,
  setShowParticipants,
  count,
}: {
  gameId: string;
  tenantId: string;
  setShowParticipants: Dispatch<SetStateAction<boolean>>;
  count: number;
}) => {
  const { t } = useTranslate();
  return (
    <LobbyReflectionContainer>
      <FluidActionButton
        style={{ position: 'sticky', top: 20, cursor: 'pointer' }}
        onClick={() => setShowParticipants(e => !e)}
      >
        <UserSvg />
        <span
          style={{
            marginLeft: 5,
            fontSize: 20,
            lineHeight: '17px',
            fontWeight: 'bold',
            color: theme.colors.black,
          }}
        >
          {count}
        </span>
      </FluidActionButton>
      <InstructionsContainer>
        <p>{t('VotingInProgress', 'Voting in progress...')}</p>
      </InstructionsContainer>
      <FluidActionButton style={{ position: 'sticky', top: 20 }}>
        <FlashSvg />
        <span
          style={{
            marginLeft: 5,
            fontSize: 20,
            lineHeight: '17px',
            fontWeight: 'bold',
            color: theme.colors.black,
          }}
        >
          0
        </span>
      </FluidActionButton>
    </LobbyReflectionContainer>
  );
};

const ParticipantCard = ({
  name,
  nickname,
  onKickParticipant,
  id,
  requireRealNames,
}: {
  id: string;
  name?: string;
  nickname: string;
  onKickParticipant: (id: string) => void;
  requireRealNames?: boolean;
}) => {
  const { t } = useTranslate();

  return (
    <ParticipantCardContainer>
      <Tooltip title={t('Remove this participant from the lesson')}>
        <StyledCrossIconContainer onClick={() => onKickParticipant(id)}>
          <CrossSvg />
        </StyledCrossIconContainer>
      </Tooltip>
      <IconWrapper style={{ marginRight: 5 }} height="20px" width="20px">
        <UserSvg />
      </IconWrapper>
      <div>
        <h3>{requireRealNames ? name || t('NoNameyet', 'No name yet...') : nickname}</h3>
        {requireRealNames && <p>{nickname}</p>}
      </div>
    </ParticipantCardContainer>
  );
};

const StyledCrossIconContainer = styled.div`
  color: ${theme.colors.black};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50px;
  padding: 5px;
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  position: absolute;
  top: 0px;
  right: 0px;
  transform: translate(50%, -50%);
  padding: 5px;
  background-color: ${theme.colors.black};
  color: white;
  svg {
    height: 12px;
    width: 12px;
  }
`;

const ParticipantCardContainer = styled.div`
  position: relative;
  display: flex;
  color: ${theme.colors.black};
  align-items: flex-start;
  padding: 10px;
  border-radius: 10px;
  background-color: white;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  h3 {
    font-weight: bold;
    margin-bottom: 2px;
    font-size: 20px;
    line-height: 20px;
  }

  p {
    font-weight: bold;
    margin-bottom: 0px;
    color: #a5a5a5;
  }
`;

const LobbyIdleView = ({
  gameId,
  requireRealNames,
  tenantId,
  participants,
  count,
}: {
  gameId: string;
  tenantId: string;
  participants: PartialParticipant[];
  requireRealNames?: boolean;
  count: number;
}) => {
  const { mutate } = useKickParticipants();

  const { mutate: kickParticipant } = useKickParticipants();
  const metrics = useMetrics();
  const { t } = useTranslate();
  const handleKickParticipant = (id: string) => {
    mutate({ gameId, participantId: id });
    kickParticipant({ gameId: gameId, participantId: id });
    metrics.logEvent('Curimote.KickParticipant', { gameId, participantId: id });
  };

  const noParticipants =
    participants !== undefined
      ? participants.filter(p => {
          if (requireRealNames && p.name === undefined) return false;
          return !p.isKicked;
        }).length === 0
      : undefined;
  return (
    <Container>
      {noParticipants === true && (
        <CuriMoteInfoContainer>
          <IconWrapper
            width={'25px'}
            height={'25px'}
            style={{
              padding: '5px 20px 0px 20px',
              cursor: 'default',
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <IdeasSvg />
          </IconWrapper>
          <p style={{ margin: 0 }}>
            <span style={{ fontWeight: 'bold' }}>{t('Moderation in Curipod')}</span>
            <br />
            {t(
              'ModerationText1',
              'This is your private moderator view. You will find the',
            )}{' '}
            <a
              href={`${window.location.origin}/${tenantId}/games/${gameId}/live`}
              target="curipod"
              style={{ color: theme.colors.black, textDecoration: 'underline' }}
            >
              {t('Live view')}
            </a>{' '}
            {t(
              'ModerationText2',
              'in a separate window or tab. Share the live view with your participants, and click start game',
            )}
          </p>
        </CuriMoteInfoContainer>
      )}
      <LobbyReflectionContainer
        style={
          noParticipants
            ? { display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'center' }
            : {}
        }
      >
        {noParticipants === true && (
          <LiveEmptyState
            icon={<AppreciateSvg />}
            description={t(
              'HereYouWillSee',
              'Here you will see participant names, and monitor responses in real time.',
            )}
            title=""
            style={{ maxWidth: '700px' }}
          />
        )}
        <ParticipantsContainer>
          {participants
            .filter(p => {
              if (requireRealNames && p.name === undefined) return false;
              return !p.isKicked;
            })
            .map(p => {
              return (
                <ParticipantCard
                  id={p.id}
                  name={p.name}
                  nickname={p.nickName}
                  key={p.id}
                  onKickParticipant={handleKickParticipant}
                  requireRealNames={requireRealNames}
                />
              );
            })}
        </ParticipantsContainer>
      </LobbyReflectionContainer>
    </Container>
  );
};
const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  margin: 10px;
`;
const ParticipantsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  & > div {
    margin: 15px 10px;
  }
`;
const CompleteView = ({
  gameId,
  tenantId,
  setShowParticipants,
  count,
}: {
  gameId: string;
  tenantId: string;
  setShowParticipants: Dispatch<SetStateAction<boolean>>;
  count: number;
}) => {
  const { t } = useTranslate();
  return (
    <LobbyReflectionContainer>
      <FluidActionButton
        style={{ position: 'sticky', top: 20, cursor: 'pointer' }}
        onClick={() => setShowParticipants(e => !e)}
      >
        <UserSvg />
        <span
          style={{
            marginLeft: 5,
            fontSize: 20,
            lineHeight: '17px',
            fontWeight: 'bold',
            color: theme.colors.black,
          }}
        >
          {count}
        </span>
      </FluidActionButton>
      <InstructionsContainer>
        <p>{t('Completed')}</p>
      </InstructionsContainer>
      <FluidActionButton style={{ position: 'sticky', top: 20 }}>
        <FlashSvg />
        <span
          style={{
            marginLeft: 5,
            fontSize: 20,
            lineHeight: '17px',
            fontWeight: 'bold',
            color: theme.colors.black,
          }}
        >
          0
        </span>
      </FluidActionButton>
    </LobbyReflectionContainer>
  );
};
const CuriMoteInfoContainer = styled.div`
  max-width: 700px;
  display: flex;
  margin: 20px;
  width: 100%;
  border-radius: 10px;
  padding: 10px;
  justify-content: space-between;
  background-color: ${theme.colors.white};
`;

const ModerationView = ({
  gameId,
  game,
  slideId,
  roundOptions,
  slideType,
  tenantId,
  currentGameMode,
  requireRealName,
  slide,
  setShowParticipants,
  participants,
  byId,
  count,
}: {
  gameId: string;
  game: Game;
  currentGameMode?: GameMode;
  slideId: string;
  requireRealName?: boolean;
  tenantId: string;
  roundOptions?: PollOptions[] | null;
  slideType: SlideType;
  slide?: Slide;
  setShowParticipants: Dispatch<SetStateAction<boolean>>;
  participants: PartialParticipant[];
  byId: ById<PartialParticipant>;
  count: number;
}) => {
  const { responses, dismissResponse: localDismiss } = useResponses(
    gameId,
    slideId,
    false,
    tenantId,
  );
  const isLargerThan600 = useBreakPoint(600);
  const isLongAnswerActivity =
    (slide?.type === 'answers' && slide?.responseLimit === 1) ||
    slide?.type === 'aiAssessedAnswers' ||
    slide?.type === 'aiAssessedImageAnswers';
  const useCurimoteNavigation = useMixpanelFlag('useCurimoteNavigation');
  const { dismissResponse } = useDismissResponse(gameId, slideId);
  const { t } = useTranslate();
  const { now } = useContext(TimesyncContext);
  const sortedParticipants = useMemo(() => {
    const sorted = [...participants];
    if (requireRealName) {
      sorted.sort(function(a, b) {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name);
        }
        return -1;
      });
    } else {
      sorted.sort(function(a, b) {
        return a.nickName.localeCompare(b.nickName);
      });
    }
    return sorted;
  }, [participants, requireRealName]);

  const sortedResponses = useMemo(() => {
    if (responses) {
      // sort the responses by participant for canvas
      if (slideType === 'canvas' || slideType === 'aiAssessedImageAnswers') {
        const responsesByParticipantId = groupBy(responses, r => r.uid);
        const localSortedResponses = sortedParticipants.flatMap(participant => {
          const responses = responsesByParticipantId[participant.id];
          return responses || [];
        });

        return localSortedResponses;
      }

      // sort by adding to bottom
      return responses
        .filter(r => r)
        .sort((a, b) => {
          if (a.timestamp && b.timestamp) {
            return a.timestamp < b.timestamp ? -1 : 1;
          }
          return 0;
        });
    }
    return [];
  }, [responses, slideType, sortedParticipants]);

  const handleDismiss = ({
    responseId,
    dismiss,
  }: {
    responseId: string;
    dismiss: boolean;
  }) => {
    dismissResponse({ responseId, dismiss });
    localDismiss({ responseId, dismiss });
  };

  const roundState = getRoundState({ game, now });

  const isAllResponsesSubmitted = useMemo(() => {
    if (sortedParticipants.length !== sortedResponses.length) {
      return false;
    }

    return sortedResponses.every(response => response.isFinished);
  }, [sortedResponses, sortedParticipants]);

  return (
    <CenteredContainer
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}
    >
      {!useCurimoteNavigation && (
        <LobbyReflectionContainer>
          <FluidActionButton
            style={{ position: 'sticky', top: 20, cursor: 'pointer' }}
            onClick={() => setShowParticipants(e => !e)}
          >
            <UserSvg />
            <span
              style={{
                marginLeft: 5,
                fontSize: 20,
                lineHeight: '17px',
                fontWeight: 'bold',
                color: theme.colors.black,
              }}
            >
              {count}
            </span>
          </FluidActionButton>
          <CenteredContainer style={{ width: '100%' }}>
            {responses.length === 0 &&
              (roundState === 'countdown' || roundState === 'in') && (
                <InstructionsContainer>
                  <p>{t('Waiting for incoming responses')}</p>
                </InstructionsContainer>
              )}
            {isLongAnswerActivity && responses.length !== 0 && !slide.wasPlayed && (
              <InstructionsContainer
                style={{ padding: isLargerThan600 ? '18px 20px' : '12px 18px' }}
              >
                {isAllResponsesSubmitted ? (
                  <p
                    style={{
                      color: theme.colors.green,
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                  >
                    <IconWrapper
                      height={'20px'}
                      width={'20px'}
                      style={{ marginRight: isLargerThan600 ? 10 : 0 }}
                    >
                      <CheckSvg />
                    </IconWrapper>{' '}
                    {isLargerThan600 && 'All responses submitted'}
                  </p>
                ) : (
                  <p>
                    <StreamingSvg />{' '}
                    {isLargerThan600 && (
                      <>
                        Streaming responses<LoadingDots>...</LoadingDots>
                      </>
                    )}
                  </p>
                )}
              </InstructionsContainer>
            )}
            {isLongAnswerActivity && responses.length !== 0 && slide.wasPlayed && (
              <InstructionsContainer style={{ padding: '18px 20px' }}>
                <p>Writing has finished</p> {/*//TODO word this better*/}
              </InstructionsContainer>
            )}
          </CenteredContainer>
          {isLongAnswerActivity && (
            <FluidActionButton style={{ position: 'sticky', top: 20 }}>
              <FlashSvg />
              <span
                style={{
                  marginLeft: 5,
                  fontSize: 20,
                  lineHeight: '17px',
                  fontWeight: 'bold',
                  color: theme.colors.black,
                }}
              >
                {responses.filter(q => q.dismissed !== true).length}
              </span>
            </FluidActionButton>
          )}
          {!isLongAnswerActivity && (
            <FluidActionButton style={{ position: 'sticky', top: 20 }}>
              <FlashSvg />
              <span
                style={{
                  marginLeft: 5,
                  fontSize: 20,
                  lineHeight: '17px',
                  fontWeight: 'bold',
                  color: theme.colors.black,
                }}
              >
                {responses.filter(q => q.dismissed !== true).length}
              </span>
            </FluidActionButton>
          )}
        </LobbyReflectionContainer>
      )}
      {slide && !isLongAnswerActivity && (
        <DismissableQuestionList
          pollOptions={roundOptions}
          onDismiss={handleDismiss}
          currentGameMode={currentGameMode}
          responses={sortedResponses}
          requireRealNames={requireRealName}
          participantsById={byId}
          slideType={slideType}
        />
      )}
      {slide &&
        isLongAnswerActivity &&
        (roundState === 'countdown' ||
          roundState === 'in' ||
          roundState === 'post-between') && (
          <StreamingLongAnswerList
            gameId={gameId}
            slideId={slideId}
            onDismiss={handleDismiss}
            responses={sortedResponses}
            requireRealNames={requireRealName}
            participantsById={byId}
            slideType={slideType}
            responseCharacterLimit={slide.responseCharacterLimit}
            roundState={roundState}
          />
        )}
    </CenteredContainer>
  );
};

const InstructionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin: 0px 10px;
  text-align: center;

  color: ${theme.colors.black};
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  user-select: none;
  border: none;
  border-radius: 20px;
  outline: none;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover {
    box-shadow: 0px 2px 40px rgba(0, 0, 0, 0.2);
  }
  background: white;
  p {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 0px;
    line-height: 17px;
  }
`;

const FluidActionButton = styled.button<{ $backgroundColor?: string; $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  height: 48px;
  color: ${({ $color }) => ($color ? $color : theme.colors.black)};
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : theme.colors.white};

  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  & svg {
    height: 20px;
    width: 20px;
  }
  user-select: none;
  border: none;
  border-radius: 20px;
  outline: none;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover {
    box-shadow: 0px 2px 40px rgba(0, 0, 0, 0.2);
  }
`;

const LobbyReflectionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex: 1 1 auto;
  padding: 10px 20px;
  position: relative;
`;

export type GameMode = 'idle' | 'inRound' | 'countdown';

const ModerationOptions = ({ pin, qrLink }: { pin: string; qrLink: string }) => {
  const [pinCopied, setPinCopied] = useState(false);
  useEffect(() => {
    const ref = setTimeout(() => {
      setPinCopied(false);
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [pinCopied]);

  const isDesktop = useBreakPoint(1000);

  return (
    <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
      <CollapseStyled
        ghost
        expandIconPosition={'right'}
        expandIcon={({ isActive }) => (
          <StyledArrowIconWrapper
            height="24px"
            width="24px"
            $isActive={isActive}
            color={theme.colors.black}
          >
            <ArrowRightSvg />
          </StyledArrowIconWrapper>
        )}
      >
        <Panel
          key="1"
          header={
            <T
              keyName="Cannot moderate on this device?"
              defaultValue="Cannot moderate on this device?"
            />
          }
        >
          <div style={{ width: '100%' }}>
            <div>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  gap: 5,
                  color: theme.colors.black,
                }}
              >
                <IconWrapper height="20px" width="20px">
                  <LaptopSvg />
                </IconWrapper>
                <span style={{ flex: '1' }}>
                  <T keyName="Another PC" defaultValue="Another PC" />
                </span>
                <CopyToClipboard
                  text={`${window.location.origin}/moderate?pin=${pin}`}
                  onCopy={() => {
                    setPinCopied(true);
                  }}
                >
                  <IconWrapper height="20px" width="20px">
                    {!pinCopied && <CopySvg />}
                    {pinCopied && <CheckSvg />}
                  </IconWrapper>
                </CopyToClipboard>
              </div>
              <div style={{ width: '100%', color: theme.colors.black }}>
                <p style={{ fontSize: 12, marginBottom: 2 }}>
                  - <T keyName="Go to" defaultValue="Go to" />{' '}
                  <CopyToClipboard
                    text={`${window.location.origin}/moderate?pin=${pin}`}
                    onCopy={() => {
                      setPinCopied(true);
                    }}
                  >
                    <span>
                      <span
                        style={{
                          color: theme.colors.black,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {window.location.origin}/moderate
                      </span>
                      {pinCopied && (
                        <span style={{ textDecoration: 'unset' }}> copied!</span>
                      )}
                    </span>
                  </CopyToClipboard>
                </p>
                <p style={{ fontSize: 12, marginBottom: 2 }}>
                  -{' '}
                  <T
                    keyName="Enter the PIN {pin}"
                    defaultValue="Enter the PIN {pin}"
                    params={{ pin }}
                  />
                </p>
              </div>
            </div>
            <div
              style={{
                width: '100%',
                background: theme.colors.black,
                height: 1,
                marginTop: 10,
                marginBottom: 10,
              }}
            ></div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'auto 130px' : 'auto 80px',
              }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    gap: 5,
                    color: theme.colors.black,
                  }}
                >
                  <IconWrapper height="20px" width="20px">
                    <MobileSvg />
                  </IconWrapper>
                  <span style={{ flex: '1' }}>
                    <T
                      keyName="iPad / Mobile device"
                      defaultValue="iPad / Mobile device"
                    />
                  </span>
                </div>
                <div style={{ width: '100%', color: theme.colors.black }}>
                  <p style={{ fontSize: 12, marginBottom: 2 }}>
                    -{' '}
                    <T
                      keyName="Scan QR code with camera"
                      defaultValue="Scan QR code with camera"
                    />
                  </p>
                </div>
              </div>

              {isDesktop ? (
                <div
                  style={{
                    padding: '5px',
                    backgroundColor: 'white',
                    width: '130px',
                    height: '130px',
                    borderRadius: '5px',
                  }}
                >
                  <QRCode value={qrLink} size={120} />
                </div>
              ) : (
                <div
                  style={{
                    padding: '5px',
                    backgroundColor: 'white',
                    width: '80px',
                    height: '80px',
                    borderRadius: '5px',
                  }}
                >
                  <QRCode value={qrLink} size={70} />
                </div>
              )}
            </div>
          </div>
        </Panel>
      </CollapseStyled>
    </div>
  );
};

const CollapseStyled = styled(Collapse)`
  background-color: transparent;
  border: none;

  .ant-collapse-arrow {
    height: 24px;
    display: flex;
    align-items: center;
    right: 0px !important;
  }

  .ant-collapse-item {
    border-bottom: none;
  }
  .ant-collapse-content {
    border-top: none;
  }
  .ant-collapse-extra {
    position: absolute;
    top: 50%;
    right: 40px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    height: 100%;
  }
  .ant-collapse-header {
    padding-right: 70px !important;
    color: ${theme.colors.black} !important;
    font-weight: bold;
    text-decoration: underline !important;
    padding: 0px !important;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const StyledArrowIconWrapper = styled(IconWrapper)<{ $isActive?: boolean }>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      && svg {
        transform: rotate(90deg);
      }
    `}
`;

const Curimote = () => {
  const { now } = useContext(TimesyncContext);
  const { t } = useTranslate();
  const metrics = useMetrics();

  const { gameId, tenantId } = useRouterMatch();

  const [showParticipantData, setShowParticipantData] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const { allParticipants: participants, count, byId } = useGameParticipants(
    gameId,
    tenantId,
  );
  const { game } = useGameListener(gameId, tenantId);
  const [currentGameMode, setCurrentGameMode] = useState<GameMode>();
  const [didRefresh, setDidRefresh] = useState(false);

  useEffect(() => {
    // If the page is refreshed, we should make sure the round continues if it is not done yet.
    if (game && !didRefresh) {
      const currentSlide = game.slides?.find(round => round.id === game.currentRound);
      if (game.roundStartsAt && currentSlide && currentSlide.durationSeconds) {
        const roundEnd = new Date(
          game.roundStartsAt + currentSlide.durationSeconds * 1000,
        );
        if (roundEnd.getTime() > now() && currentGameMode !== 'inRound') {
          setCurrentGameMode('inRound');
          setDidRefresh(true);
        }
      }
    }
  }, [game, currentGameMode, now, didRefresh]);

  useEffect(() => {
    if (currentGameMode === undefined && game) {
      if (game && !game.isPlaying) {
        setCurrentGameMode('idle');
      }
      // Check whether
      const currentSlide = game.slides?.find(round => round.id === game.currentRound);
      if (game.roundStartsAt && currentSlide && currentSlide.durationSeconds) {
        const roundEnd = new Date(
          game.roundStartsAt + currentSlide.durationSeconds * 1000,
        );
        if (roundEnd.getTime() < now()) {
          // The game should be in idle
          setCurrentGameMode('idle');
        }
      }
    }
  }, [game, currentGameMode, now]);

  const isInLobby = game && !game.isPlaying && !game.isComplete;
  const slide = game?.slides?.find(r => r.id === game.currentRound);
  useEffect(() => {
    if (game?.isShowingLobby) {
      setShowParticipants(true);
    } else {
      setShowParticipants(false);
    }
  }, [game]);

  const handleEnterPress = () => {
    metrics.logEvent('Curimote.ShowParticipantData');
    setShowParticipantData(true);
  };

  return (
    <GameContainer>
      <Hotkeys keyName="Enter" onKeyUp={handleEnterPress} />
      <Head>{game && <title>{`Curipod Moderation Tool | ${game.title}`}</title>}</Head>
      {!showParticipantData && (
        <ActivationOverlay>
          <ActivateCurimoteContainer>
            <IconWrapper
              height={'40px'}
              width={'40px'}
              color={theme.colors.orange}
              style={{ padding: '10px', cursor: 'default' }}
            >
              <EyeOffSvg />
            </IconWrapper>
            <h1 style={{ textAlign: 'center' }}>
              {t('moderationView', 'Moderation View')}
            </h1>
            <p
              style={{
                marginBottom: '20px',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
                width: '400px',
              }}
            >
              {t(
                'teacherwindowexplanation4',
                'Place this window on your private screen, and share the live view on the shared screen. ',
              )}
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                marginBottom: 20,
              }}
            >
              <Button
                width="100px"
                onClick={() => {
                  metrics.logEvent('Curimote.ShowParticipantData');
                  setShowParticipantData(true);
                }}
              >
                {t('Ok')}
              </Button>
            </div>
            {game?.pin && (
              <ModerationOptions pin={game.pin} qrLink={window.location.href} />
            )}
          </ActivateCurimoteContainer>
        </ActivationOverlay>
      )}
      <TopBar $hideShadow={isInLobby}>
        <div></div>
        <TopBarTitle>{t('Teacher view (for your eyes only)')}</TopBarTitle>
        <ActionBar
          onShowParticipantsToggled={() => setShowParticipants(bool => !bool)}
          isShowingParticipants={showParticipants}
          gameId={gameId}
        />
      </TopBar>

      <GameContentContainer>
        <>
          {!game && (
            <CenteredContainer>
              <Loader />
            </CenteredContainer>
          )}
          {game && ((!game.isPlaying && !game.isComplete) || showParticipants) && (
            <LobbyIdleView
              gameId={gameId}
              participants={participants}
              tenantId={tenantId}
              requireRealNames={game.requireRealNames}
              count={count}
            />
          )}
          {game &&
            game.isPlaying &&
            game.currentRound &&
            !showParticipants &&
            game.slides?.find(r => r.id === game.currentRound)?.type !== 'voting' && (
              <ModerationView
                gameId={gameId}
                game={game}
                currentGameMode={currentGameMode}
                tenantId={tenantId}
                requireRealName={game.requireRealNames}
                roundOptions={slide?.pollOptions}
                slideId={game.currentRound}
                setShowParticipants={setShowParticipants}
                slide={slide}
                participants={participants}
                count={count}
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                slideType={slide?.type!}
                byId={byId}
              />
            )}
          {game &&
            game.isPlaying &&
            !showParticipants &&
            game.slides?.find(r => r.id === game.currentRound)?.type === 'voting' && (
              <VotingView
                gameId={gameId}
                tenantId={tenantId}
                setShowParticipants={setShowParticipants}
                count={count}
              />
            )}
          {game && game.isComplete && (
            <CompleteView
              gameId={gameId}
              tenantId={tenantId}
              setShowParticipants={setShowParticipants}
              count={count}
            />
          )}
        </>
      </GameContentContainer>
    </GameContainer>
  );
};
export const ActivateCurimoteContainer = styled.div`
  background: #f9f3eb;
  border-radius: 20px;
  padding: 50px;

  h1 {
    font-weight: bold;
  }

  p {
    font-weight: bold;
  }
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
`;
const TopBarTitle = styled.span`
  flex: 1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 28px;
  text-align: center;
  font-weight: 600;
  font-size: 30px;
  @media (max-width: 600px) {
    display: none;
  }
`;

const GameContentContainer = styled.div`
  height: calc(100% - 100px);
  margin-top: 100px;
  overflow-y: auto;
`;

const GameContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

const TopBar = styled.div<{ $hideShadow?: boolean }>`
  position: fixed;
  z-index: ${zIndexes('PRESENTER_TOP_BAR')};
  height: 100px;
  width: 100%;
  top: 0;
  left: 0;
  padding: 0 20px;

  @media (min-width: 300px) {
    padding: 0 20px;
  }
  @media (min-width: 420px) {
    padding: 0 20px;
  }
  @media (min-width: 600px) {
    padding: 0 20px;
  }
  @media (min-width: 992px) {
    padding: 0 20px;
  }
  @media (min-width: 1200px) {
    padding: 0 20px;
  }
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};
  ${({ $hideShadow }) => !$hideShadow && 'box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CurimotePage = () => {
  const session = useSession();
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined>(undefined);
  const { gameId, tenantId } = useRouterMatch();

  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      const gameAPI = new GameAPI(session.data?.idToken as string);
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);
  if (!session || !gameAPI || !session.data?.user.uid)
    return (
      <CenteredContainer style={{ height: '100%' }}>
        <Loader />
      </CenteredContainer>
    );
  return (
    <>
      <SocketProvider gameAPI={gameAPI}>
        <PoorInternetStatusProvider
          tenantId={tenantId}
          uid={session.data?.user.uid}
          gameAPI={gameAPI}
          gameId={gameId}
        >
          <TimesyncProvider gameAPI={gameAPI} gameId={gameId}>
            <Curimote />
          </TimesyncProvider>
        </PoorInternetStatusProvider>
      </SocketProvider>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};

const CurimotePageExport = () => {
  return (
    <NoAccess>
      <CurimotePage />
    </NoAccess>
  );
};
export default CurimotePageExport;
